import { shellPaths, TABS_PREFIX } from '@/core/router/app-paths';

const LEAD_DETAIL_LEGACY = /^\/leads\/([^/?#]+)/;
const PUBLIC_PROFILE_LEGACY = /^\/public-profile\/([^/]+)\/([^/?#]+)/;
const PROFILES_PUBLIC_LEGACY = /^\/tabs\/profiles\/public\/([^/]+)\/([^/?#]+)/;
const STANDALONE_PATH_PREFIXES = [
  '/auth',
  '/onboarding',
  '/claim/verify',
  '/link-error',
  '/workspace/invitation/',
  '/error',
] as const;

/**
 * Normalizes notification deep links for in-app navigation.
 * Handles legacy full URLs and `/leads/:id` paths stored by older backends.
 */
export function resolveNotificationDeepLink(deepLink: string): string {
  const trimmed = deepLink.trim();
  if (!trimmed) {
    return trimmed;
  }

  let path = trimmed;
  let suffix = '';

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      path = url.pathname;
      suffix = `${url.search}${url.hash}`;
    } catch {
      return trimmed;
    }
  } else {
    const queryIndex = trimmed.indexOf('?');
    const hashIndex = trimmed.indexOf('#');
    const cutIndex = Math.min(
      queryIndex >= 0 ? queryIndex : Number.POSITIVE_INFINITY,
      hashIndex >= 0 ? hashIndex : Number.POSITIVE_INFINITY,
    );
    if (Number.isFinite(cutIndex)) {
      path = trimmed.slice(0, cutIndex);
      suffix = trimmed.slice(cutIndex);
    }
  }

  const leadMatch = path.match(LEAD_DETAIL_LEGACY);
  if (leadMatch?.[1]) {
    return `${shellPaths.leadDetail(leadMatch[1])}${suffix}`;
  }

  const publicProfileMatch = path.match(PUBLIC_PROFILE_LEGACY);
  if (publicProfileMatch?.[1] && publicProfileMatch?.[2]) {
    return `${shellPaths.leadsPublicProfilePath(
      decodeURIComponent(publicProfileMatch[1]),
      decodeURIComponent(publicProfileMatch[2]),
    )}${suffix}`;
  }

  const profilesPublicMatch = path.match(PROFILES_PUBLIC_LEGACY);
  if (profilesPublicMatch?.[1] && profilesPublicMatch?.[2]) {
    return `${shellPaths.leadsPublicProfilePath(
      decodeURIComponent(profilesPublicMatch[1]),
      decodeURIComponent(profilesPublicMatch[2]),
    )}${suffix}`;
  }

  if (path === `${TABS_PREFIX}/profile` || path === '/profile') {
    return `${shellPaths.profiles}${suffix}`;
  }

  if (path.startsWith(TABS_PREFIX)) {
    return `${path}${suffix}`;
  }

  if (
    STANDALONE_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(prefix),
    )
  ) {
    return `${path}${suffix}`;
  }

  if (path.startsWith('/')) {
    return `${TABS_PREFIX}${path}${suffix}`;
  }

  return `${TABS_PREFIX}/${path}${suffix}`;
}
